import React, { useState } from 'react';
import { Modal, Upload, Select, Button, message, Form, Typography, Radio, Space, Alert } from 'antd';
import { InboxOutlined, ThunderboltOutlined, RocketOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

const { Dragger } = Upload;
const { Option } = Select;
const { Title, Text } = Typography;

const UploadOrderReportModal = ({ 
    visible, 
    onCancel, 
    onUpload,
    onTriggerSync, // For trigger sync function
    onTriggerOrderProcessing // New prop for trigger order processing function
}) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [mode, setMode] = useState('upload'); // 'upload' or 'trigger' or 'process'
    const sellerAccounts = useSelector(state => state.dataCommon.sellerAccounts);

    const uploadProps = {
        name: 'file',
        multiple: false,
        fileList,
        accept: '.csv,.txt',
        beforeUpload: (file) => {
            // Validate file type
            const isValidFile = file.type === 'text/csv' || 
                               file.type === 'text/plain' || 
                               file.name.toLowerCase().endsWith('.csv') || 
                               file.name.toLowerCase().endsWith('.txt');
            if (!isValidFile) {
                message.error('Chỉ chấp nhận file CSV hoặc TXT!');
                return false;
            }

            // Validate file size (max 50MB)
            const isLt50M = file.size / 1024 / 1024 < 50;
            if (!isLt50M) {
                message.error('File phải nhỏ hơn 50MB!');
                return false;
            }

            setFileList([file]);
            return false; // Prevent auto upload
        },
        onRemove: () => {
            setFileList([]);
        }
    };

    const handleSubmit = async (values) => {
        const { sellerId } = values;

        if (mode === 'upload') {
            // Upload mode - existing functionality
            if (fileList.length === 0) {
                message.error('Vui lòng chọn file CSV để upload!');
                return;
            }

            const file = fileList[0];

            // Create FormData for file upload
            const formData = new FormData();
            formData.append('file', file);
            formData.append('sellerId', sellerId);
            // Use filename without extension as batch ID
            const batchId = file.name.replace(/\.(csv|txt)$/i, '');
            formData.append('batchId', batchId);

            setUploading(true);
            try {
                await onUpload(formData);
                form.resetFields();
                setFileList([]);
                setMode('upload'); // Reset mode
                onCancel();
            } catch (error) {
                console.error('Upload error:', error);
            } finally {
                setUploading(false);
            }
        } else if (mode === 'trigger') {
            // Trigger sync mode - sync Amazon Order Reports for specific seller
            setUploading(true);
            try {
                if (onTriggerSync) {
                    await onTriggerSync(sellerId);
                    form.resetFields();
                    setMode('upload'); // Reset mode
                    onCancel();
                } else {
                    message.error('Trigger sync function not available');
                }
            } catch (error) {
                console.error('Trigger sync error:', error);
            } finally {
                setUploading(false);
            }
        } else if (mode === 'process') {
            // Trigger order processing mode - process order temp data
            setUploading(true);
            try {
                if (onTriggerOrderProcessing) {
                    await onTriggerOrderProcessing();
                    form.resetFields();
                    setMode('upload'); // Reset mode
                    onCancel();
                } else {
                    message.error('Trigger order processing function not available');
                }
            } catch (error) {
                console.error('Trigger order processing error:', error);
            } finally {
                setUploading(false);
            }
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setFileList([]);
        setMode('upload'); // Reset mode to default
        onCancel();
    };

    return (
        <Modal
            title={
                <div>
                    <Title level={4} style={{ margin: 0 }}>
                        {mode === 'upload' ? 'Upload Order Report CSV' : 
                         mode === 'trigger' ? 'Trigger Amazon Order Reports Sync' : 
                         'Trigger Order Processing'}
                    </Title>
                    <Text type="secondary">
                        {mode === 'upload' 
                            ? 'Tải lên file CSV report từ Amazon Seller Central'
                            : mode === 'trigger'
                            ? 'Trigger worker scrape Amazon Order Reports cho seller account'
                            : 'Trigger xử lý các orders trong order processing temp'
                        }
                    </Text>
                </div>
            }
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={600}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                style={{ marginTop: 16 }}
            >
                {/* Mode Selection */}
                <Form.Item
                    label="Chế độ xử lý"
                >
                    <Radio.Group 
                        value={mode} 
                        onChange={(e) => {
                            setMode(e.target.value);
                            setFileList([]); // Clear file list when switching modes
                        }}
                        style={{ width: '100%' }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Radio value="upload" style={{ width: '100%' }}>
                                <div>
                                    <Text strong>Upload CSV File</Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Tải lên file CSV/TXT từ Amazon và xử lý trực tiếp
                                    </Text>
                                </div>
                            </Radio>
                            <Radio value="trigger" style={{ width: '100%' }}>
                                <div>
                                    <Text strong><ThunderboltOutlined /> Trigger Worker Sync</Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Kích hoạt worker scrape Amazon Order Reports tự động
                                    </Text>
                                </div>
                            </Radio>
                            <Radio value="process" style={{ width: '100%' }}>
                                <div>
                                    <Text strong><RocketOutlined /> Trigger Order Processing</Text>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Xử lý các orders đang chờ trong order processing temp
                                    </Text>
                                </div>
                            </Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Form.Item
                    label="Seller Account"
                    name="sellerId"
                    rules={[
                        { 
                            required: mode !== 'process', // Only required for upload and trigger modes
                            message: 'Vui lòng chọn seller account!' 
                        }
                    ]}
                    style={{ display: mode === 'process' ? 'none' : 'block' }}
                >
                    <Select
                        placeholder="Chọn seller account"
                        showSearch
                        loading={!sellerAccounts || sellerAccounts.length === 0}
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        disabled={mode === 'process'}
                    >
                        {sellerAccounts?.map(account => (
                            <Option key={account.id} value={account.id}>
                                {account.name} ({account.sellerId})
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {mode === 'upload' && (
                    <Form.Item
                        label="Order Report CSV/TXT File"
                        required
                    >
                        <Dragger {...uploadProps}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Kéo thả file CSV/TXT vào đây hoặc click để chọn file
                            </p>
                            <p className="ant-upload-hint">
                                Chấp nhận file CSV hoặc TXT từ Amazon Order Reports. 
                                File phải có cấu trúc cột tương tự báo cáo tự động.
                            </p>
                        </Dragger>
                    </Form.Item>
                )}

                {mode === 'trigger' && (
                    <Alert
                        type="info"
                        showIcon
                        message="Trigger Worker Sync"
                        description={
                            <div>
                                <p>Worker sẽ được kích hoạt để:</p>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    <li>Truy cập Amazon Seller Central</li>
                                    <li>Scrape danh sách Order Reports có sẵn</li>
                                    <li>Download và xử lý các reports mới</li>
                                    <li>Tự động tạo orders và customizations</li>
                                </ul>
                            </div>
                        }
                        style={{ marginBottom: 16 }}
                    />
                )}

                {mode === 'process' && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Trigger Order Processing"
                        description={
                            <div>
                                <p>Main worker sẽ xử lý:</p>
                                <ul style={{ margin: 0, paddingLeft: 16 }}>
                                    <li>Tất cả orders đang chờ trong order_processing_temp</li>
                                    <li>Tạo order records chính thức từ temp data</li>
                                    <li>Download customization files nếu có</li>
                                    <li>Tạo Trello cards cho orders có customization</li>
                                    <li>Cleanup temp data sau khi xử lý xong</li>
                                </ul>
                                <p style={{ marginTop: 8, marginBottom: 0 }}>
                                    <strong>Lưu ý:</strong> Không cần chọn seller account vì sẽ xử lý tất cả temp orders
                                </p>
                            </div>
                        }
                        style={{ marginBottom: 16 }}
                    />
                )}

                <div style={{ marginTop: 24 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <strong>Lưu ý:</strong><br />
                        {mode === 'upload' ? (
                            <>
                                • File sẽ được xử lý và tạo orders tương tự quá trình tự động<br />
                                • Batch ID sẽ được tạo từ tên file (không bao gồm .csv/.txt)<br />
                                • Các customization files sẽ được download và xử lý như bình thường<br />
                                • Trello cards sẽ được tạo cho các orders có customization
                            </>
                        ) : mode === 'trigger' ? (
                            <>
                                • Worker phải online và được assign cho seller account<br />
                                • Job sẽ chạy ngay lập tức với priority cao<br />
                                • Chỉ các reports mới sẽ được download và xử lý<br />
                                • Có thể mất vài phút để hoàn tất quá trình
                            </>
                        ) : (
                            <>
                                • Main worker sẽ xử lý tất cả orders trong temp table<br />
                                • Job chạy ngay lập tức với priority cao<br />
                                • Temp data sẽ được cleanup sau khi xử lý xong<br />
                                • Thích hợp khi có nhiều orders chờ xử lý
                            </>
                        )}
                    </Text>
                </div>

                <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={handleCancel}>
                            Hủy
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit"
                            loading={uploading}
                            disabled={mode === 'upload' && fileList.length === 0}
                            icon={mode === 'trigger' ? <ThunderboltOutlined /> : mode === 'process' ? <RocketOutlined /> : null}
                        >
                            {mode === 'upload' ? 'Upload & Process' : 
                             mode === 'trigger' ? 'Trigger Sync' : 
                             'Trigger Processing'}
                        </Button>
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default UploadOrderReportModal;
